import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';
import {
  assertPogiAjaxCart,
  fetchPogiJsonWithTimeout,
  withPogiCartMutationLock,
} from '@pogi/cart-mutation';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];
  #sectionRecoveryPromise = null;
  #pending = false;

  /**
   * Keeps discount controls focusable while the pending guard prevents another mutation.
   * @returns {() => void} Restores the controls' previous states.
   */
  #disableControls() {
    const previousBusy = this.getAttribute('aria-busy');
    const input = this.querySelector('input[name="discount"]');
    const previousReadOnly = input instanceof HTMLInputElement && input.readOnly;
    const buttons = Array.from(this.querySelectorAll('button'), (button) => ({
      button,
      previousDisabled: button.getAttribute('aria-disabled'),
    }));

    this.setAttribute('aria-busy', 'true');
    if (input instanceof HTMLInputElement) input.readOnly = true;
    for (const { button } of buttons) button.setAttribute('aria-disabled', 'true');

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (input instanceof HTMLInputElement) input.readOnly = previousReadOnly;
      for (const { button, previousDisabled } of buttons) {
        if (previousDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previousDisabled);
      }
    };
  }



  #cartCodes(cart) {
    return (cart?.discount_codes || [])
      .map((discount) => String(discount?.code || '').trim().toLowerCase())
      .filter(Boolean)
      .sort();
  }

  #codesMatch(cart, desiredCodes) {
    const current = this.#cartCodes(cart);
    const desired = desiredCodes.map((code) => code.trim().toLowerCase()).filter(Boolean).sort();
    return current.length === desired.length && current.every((code, index) => code === desired[index]);
  }

  async #fetchCart() {
    const { response, payload } = await fetchPogiJsonWithTimeout(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
    return assertPogiAjaxCart(payload);
  }

  async #mutateDiscount(action, discountCode) {
    return withPogiCartMutationLock(async () => {
      const beforeCart = await this.#fetchCart();
      const currentCodes = (beforeCart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
      const desiredCodes = action === 'add'
        ? [...new Set([...currentCodes, discountCode])]
        : currentCodes.filter((code) => code.toLowerCase() !== discountCode.toLowerCase());

      if (this.#codesMatch(beforeCart, desiredCodes)) {
        return { cart: beforeCart, html: null, recovered: false, desiredCodes };
      }

      let commitState = 'unknown';
      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: desiredCodes.join(','),
            sections: [this.dataset.sectionId],
          }),
        });
        const { response, payload } = await fetchPogiJsonWithTimeout(Theme.routes.cart_update_url, config);
        if (!response.ok) {
          commitState = 'rejected';
          const error = new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
          error.response = payload;
          throw error;
        }
        const cart = assertPogiAjaxCart(payload);
        const html = cart.sections?.[this.dataset.sectionId];
        if (typeof html !== 'string' || html.trim() === '') {
          throw new Error('Cart response did not include the requested section');
        }
        return { cart, html, recovered: false, desiredCodes };
      } catch (error) {
        let currentCart = null;
        try {
          currentCart = await this.#fetchCart();
        } catch (_) {
          // Preserve the mutation error when authoritative recovery is unavailable.
        }
        if (commitState !== 'rejected' && currentCart && this.#codesMatch(currentCart, desiredCodes)) {
          return { cart: currentCart, html: null, recovered: true, desiredCodes };
        }
        error.currentCart = currentCart;
        error.commitState = commitState;
        error.retrySafe = commitState === 'rejected';
        throw error;
      }
    });
  }

  /**
   * @param {string | null} html - Optional section bundled with the committed mutation.
   * @param {(html: string) => boolean} [validateFreshHtml] - Validates fallback Liquid before it replaces the UI.
   */
  async #renderDiscountSection(html, validateFreshHtml) {
    let active = true;
    let timeout;
    try {
      await Promise.race([
        (async () => {
          while (active && this.isConnected) {
            let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
            while (active && pendingQuantityUpdates.length) {
              await Promise.all(pendingQuantityUpdates);
              // Quantity changes supersede the discount response's earlier HTML.
              html = null;
              pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
            }
            if (!active || !this.isConnected) return;
            if (html) {
              sectionRenderer.abortRender(this.dataset.sectionId);
              await morphSection(this.dataset.sectionId, html, {
                mode: this.closest('theme-drawer') ? 'hydration' : 'full',
              });
              return;
            }
            let validated = false;
            await sectionRenderer.renderSection(this.dataset.sectionId, {
              cache: false,
              mode: this.closest('theme-drawer') ? 'hydration' : 'full',
              shouldRender: (freshHtml) => {
                if (!active) return false;
                validated = true;
                return validateFreshHtml?.(freshHtml) ?? true;
              },
            });
            // An aborted renderer resolves without morphing. Wait for any newer
            // quantity work and retry instead of reporting a stale view as success.
            if (validated) return;
          }
        })(),
        new Promise((_, reject) => {
          timeout = window.setTimeout(() => {
            active = false;
            const error = new Error('Discount section refresh timed out');
            error.name = 'TimeoutError';
            reject(error);
          }, 10000);
        }),
      ]);
    } finally {
      active = false;
      window.clearTimeout(timeout);
    }
  }

  /**
   * Reconciles an uncertain discount view without allowing an unbounded render
   * promise to strand the component's controls forever.
   */
  recoverSection = async () => {
    if (this.#sectionRecoveryPromise) return this.#sectionRecoveryPromise;
    this.#sectionRecoveryPromise = (async () => {
      try {
        await this.#renderDiscountSection(null);
        delete this.dataset.mutationUncertain;
        delete this.dataset.sectionRecoveryFailed;
        for (const control of this.querySelectorAll('button, input')) control.disabled = false;
        return true;
      } catch (error) {
        this.dataset.sectionRecoveryFailed = 'true';
        // The cart write may already be committed. Fail closed to Shopify's
        // localized authoritative cart instead of leaving stale controls
        // disabled indefinitely on either the drawer or full-cart surface.
        window.setTimeout(() => window.location.assign(Theme.routes.cart_url), 250);
        throw error;
      } finally {
        this.#sectionRecoveryPromise = null;
      }
    })();
    return this.#sectionRecoveryPromise;
  };

  #resetDiscountErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    for (const target of [cartDiscountErrorDiscountCode, cartDiscountErrorShipping]) {
      if (target.dataset.originalText) {
        target.textContent = target.dataset.originalText;
        delete target.dataset.originalText;
      }
      target.classList.add('hidden');
    }
    cartDiscountError.classList.add('hidden');
  }

  #settleMutationError(deferredPromise, error) {
    if (error?.currentCart) {
      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(error.currentCart),
        detail: {
          didError: true,
          recovered: true,
          commitState: error.commitState,
          retrySafe: error.retrySafe,
        },
      });
    } else {
      deferredPromise.reject(error);
    }

    const message = Theme.translations.cart_update_error || 'Unable to update your cart. Please try again.';
    const target = this.refs.cartDiscountErrorDiscountCode;
    if (!target.dataset.originalText) target.dataset.originalText = target.textContent;
    target.textContent = message;
    this.refs.cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'SERVICE_UNAVAILABLE',
        detail: {
          technicalMessage: error?.message,
          commitState: error?.commitState,
          retrySafe: error?.retrySafe,
        },
      })
    );

    if (error?.retrySafe === false) {
      this.dataset.mutationUncertain = 'true';
      for (const control of this.querySelectorAll('button, input')) control.disabled = true;
      setTimeout(() => {
        this.recoverSection().catch((sectionError) => {
          console.warn('[cart-discount] Delayed recovery failed:', sectionError);
        });
      }, 2500);
    }
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#pending || this.dataset.mutationUncertain === 'true' || this.dataset.sectionRecoveryFailed === 'true') return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    this.#resetDiscountErrors();

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    this.#pending = true;
    const restoreControls = this.#disableControls();
    sectionRenderer.abortRender(this.dataset.sectionId);

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const result = await this.#mutateDiscount('add', discountCodeValue);
      const data = result.cart;

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          detail: { didError: true, recovered: result.recovered },
        });
        return;
      }

      const newHtml = result.html;
      // Shopify may report an applicable shipping code while Liquid omits its
      // pill. Check both bundled and recovered HTML so the localized explanation
      // survives a null section response or a superseding quantity update.
      /** @param {string} html @returns {boolean} */
      const shouldRender = (html) => {
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            return false;
          }
        }
        return true;
      };
      if (newHtml && !shouldRender(newHtml)) {
        discountCode.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          detail: { didError: true, recovered: result.recovered },
        });
        return;
      }

      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      // Settle the committed write before waiting for later quantity renders.
      // Otherwise its older count could arrive after a newer quantity event.
      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          didError: false,
          recovered: result.recovered,
          sections: data.sections,
        },
      });
      try {
        await this.#renderDiscountSection(newHtml, shouldRender);
      } catch (renderError) {
        renderError.currentCart = data;
        renderError.commitState = 'committed';
        renderError.retrySafe = false;
        throw renderError;
      }

    } catch (error) {
      this.#settleMutationError(deferredPromise, error);
    } finally {
      this.#pending = false;
      restoreControls();
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#pending || this.dataset.mutationUncertain === 'true' || this.dataset.sectionRecoveryFailed === 'true') return;

    const isActivation =
      event instanceof MouseEvent ||
      (event instanceof KeyboardEvent && event.key === 'Enter');
    if (
      !isActivation ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const focusedPill = pill.contains(document.activeElement);
    const pillIndex = Array.from(this.querySelectorAll('.cart-discount__pill')).indexOf(pill);

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    this.#pending = true;
    const restoreControls = this.#disableControls();
    sectionRenderer.abortRender(this.dataset.sectionId);

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const result = await this.#mutateDiscount('remove', discountCode);
      const data = result.cart;

      // Settle the committed write before waiting for later quantity renders.
      // Otherwise its older count could arrive after a newer quantity event.
      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          didError: false,
          recovered: result.recovered,
          sections: data.sections,
        },
      });
      try {
        await this.#renderDiscountSection(result.html);
      } catch (renderError) {
        renderError.currentCart = data;
        renderError.commitState = 'committed';
        renderError.retrySafe = false;
        throw renderError;
      }

    } catch (error) {
      this.#settleMutationError(deferredPromise, error);
    } finally {
      this.#pending = false;
      restoreControls();
      if (focusedPill) this.#restoreDiscountFocus(pillIndex);
    }
  };

  #restoreDiscountFocus(index) {
    const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const root = section || this;
    const pills = Array.from(root.querySelectorAll('.cart-discount__pill'));
    const targetPill = pills[Math.min(Math.max(index, 0), pills.length - 1)];
    const preferred = targetPill instanceof HTMLElement
      ? targetPill.querySelector('button:not([disabled]):not([hidden])')
      : null;
    let target = preferred instanceof HTMLElement && !preferred.closest('[hidden]') ? preferred : null;
    target ||= root.querySelector('input[name="discount"]:not([disabled]):not([hidden])');
    target ||= root.querySelector('details summary:not([hidden])');
    if (
      target instanceof HTMLElement &&
      !target.matches(':disabled, [hidden]') &&
      !target.closest('[hidden]')
    ) {
      target.focus({ preventScroll: true });
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = target.textContent?.trim() || Theme.translations.cart_update_error;
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
